/**
 * Iter 70 — Roles & Access Control admin UI.
 *
 * Visible only to super_admin + IT department (frontend gates; backend also
 * enforces). Lets admins:
 *   • View / edit existing roles' module × action matrix
 *   • Create a brand-new custom role
 *   • Delete custom (non-system) roles
 *   • Edit a role's name / grade / department / description
 *
 * Renders 22 modules (grouped by category) × 6 actions (read/write/approve/
 * delete/email/export) as a sticky-header table of checkboxes.
 */
import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ShieldAlert, Plus, Trash2, Save, Loader2, X, Check, Copy, Users as UsersIcon, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import PageHeader from '../../components/PageHeader';
import { apiGet, apiPost, apiPut, apiDel } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';

export default function RolesAccessControl() {
  const navigate = useNavigate();
  const { user } = useAuth() || {};
  const canEdit = (() => {
    const role = (user?.role || '').toLowerCase();
    const dept = (user?.department || '').toUpperCase();
    return role === 'super_admin' || dept === 'IT';
  })();

  const [catalog, setCatalog] = useState({ modules: [], actions: [] });
  const [roles, setRoles] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [departments, setDepartments] = useState([]);
  const [grades, setGrades] = useState([]);
  // Users assigned to the currently selected role + reassignment UI
  const [roleUsers, setRoleUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [reassignFor, setReassignFor] = useState(null); // user_id being reassigned
  const [reassignTarget, setReassignTarget] = useState('');
  // Per-user permission override editor (modal)
  const [permEditFor, setPermEditFor] = useState(null); // { user_id, name, email, role_name }
  const [permEditMatrix, setPermEditMatrix] = useState({}); // { module_key: { action: bool } }
  const [permEditRoleBase, setPermEditRoleBase] = useState({}); // for diff display
  const [permEditOverrides, setPermEditOverrides] = useState({}); // existing overrides
  const [permEditLoading, setPermEditLoading] = useState(false);
  const [permEditSaving, setPermEditSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [cat, rs, masters] = await Promise.all([
        apiGet('/rbac/catalog'),
        apiGet('/rbac/roles'),
        apiGet('/config-masters').catch(() => ({ masters: {} })),
      ]);
      setCatalog(cat || { modules: [], actions: [] });
      setRoles(rs?.rows || []);
      setDepartments(masters?.masters?.departments || []);
      setGrades(masters?.masters?.employee_grades || []);
      if (!selectedId && rs?.rows?.[0]) setSelectedId(rs.rows[0].id);
    } catch (e) {
      toast.error('Failed to load roles');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // Whenever the selected role changes, load its assigned users.
  useEffect(() => {
    if (!selectedId) { setRoleUsers([]); return; }
    setLoadingUsers(true);
    apiGet(`/rbac/roles/${selectedId}/users`)
      .then(r => setRoleUsers(r?.users || []))
      .catch(() => setRoleUsers([]))
      .finally(() => setLoadingUsers(false));
  }, [selectedId]);

  const reassignUserRole = async (userId, targetRoleId) => {
    if (!targetRoleId) { toast.error('Pick a target role'); return; }
    try {
      if (targetRoleId === '_unassigned') {
        await apiDel(`/rbac/users/${userId}/role`);
      } else {
        await apiPut(`/rbac/users/${userId}/role`, { role_id: targetRoleId });
      }
      toast.success('Role reassigned');
      setReassignFor(null);
      setReassignTarget('');
      // Refresh the current role's users list
      const r = await apiGet(`/rbac/roles/${selectedId}/users`);
      setRoleUsers(r?.users || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reassignment failed');
    }
  };

  // ── Iter 105.2 — Per-user permission editor ────────────────────────────
  const openPermEditor = async (u) => {
    setPermEditFor({ user_id: u.user_id, name: u.name || u.email, email: u.email, role_name: u.role_name || '—' });
    setPermEditLoading(true);
    setPermEditMatrix({});
    setPermEditRoleBase({});
    setPermEditOverrides({});
    try {
      const res = await apiGet(`/rbac/users/${u.user_id}/permissions`);
      setPermEditMatrix(res?.effective || {});
      setPermEditRoleBase(res?.from_role || {});
      setPermEditOverrides(res?.overrides || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load user permissions');
      setPermEditFor(null);
    } finally {
      setPermEditLoading(false);
    }
  };

  const togglePermEditCell = (modKey, action) => {
    setPermEditMatrix((m) => ({
      ...m,
      [modKey]: { ...(m[modKey] || {}), [action]: !m[modKey]?.[action] },
    }));
  };

  const savePermEdit = async () => {
    if (!permEditFor) return;
    setPermEditSaving(true);
    try {
      const res = await apiPut(`/rbac/users/${permEditFor.user_id}/permissions`, { permissions: permEditMatrix });
      const cnt = res?.override_count || 0;
      toast.success(cnt > 0 ? `Saved ${cnt} custom override${cnt === 1 ? '' : 's'}` : 'Cleared all overrides — user follows the role');
      setPermEditFor(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setPermEditSaving(false);
    }
  };

  const resetPermEdit = async () => {
    if (!permEditFor) return;
    if (!window.confirm(`Reset ${permEditFor.name}'s permissions to follow the role exactly?\n\nAll custom cell-level overrides will be removed.`)) return;
    setPermEditSaving(true);
    try {
      await apiDel(`/rbac/users/${permEditFor.user_id}/permissions`);
      toast.success('Reset to role defaults');
      setPermEditFor(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reset failed');
    } finally {
      setPermEditSaving(false);
    }
  };

  const selected = roles.find((r) => r.id === selectedId);
  const isSuperAdminSelected = selectedId === 'role_super_admin';

  // Categories for the matrix (preserves order from catalog)
  const grouped = useMemo(() => {
    const map = {};
    for (const m of catalog.modules || []) {
      if (!map[m.category]) map[m.category] = [];
      map[m.category].push(m);
    }
    return map;
  }, [catalog.modules]);

  const filteredRoles = roles.filter((r) =>
    !search || r.name.toLowerCase().includes(search.toLowerCase())
    || (r.department || '').toLowerCase().includes(search.toLowerCase()));

  // === Mutations ===
  const togglePerm = (modKey, action) => {
    if (!canEdit || isSuperAdminSelected) return;
    setRoles((rs) => rs.map((r) => {
      if (r.id !== selectedId) return r;
      const next = { ...(r.permissions || {}) };
      next[modKey] = { ...(next[modKey] || {}) };
      next[modKey][action] = !next[modKey][action];
      return { ...r, permissions: next, _dirty: true };
    }));
  };

  const setAllForRow = (modKey, value) => {
    if (!canEdit || isSuperAdminSelected) return;
    setRoles((rs) => rs.map((r) => {
      if (r.id !== selectedId) return r;
      const next = { ...(r.permissions || {}) };
      next[modKey] = Object.fromEntries(catalog.actions.map((a) => [a, value]));
      return { ...r, permissions: next, _dirty: true };
    }));
  };

  // BUILD 2: scope + approval limit helpers
  const setModuleScope = (modKey, scope) =>
    setRoles((rs) => rs.map((r) => r.id !== selectedId ? r : {
      ...r,
      permissions: { ...r.permissions, [modKey]: { ...(r.permissions?.[modKey] || {}), scope } },
      _dirty: true,
    }));

  const setApproveLimit = (modKey, value) =>
    setRoles((rs) => rs.map((r) => r.id !== selectedId ? r : {
      ...r,
      permissions: {
        ...r.permissions,
        [modKey]: { ...(r.permissions?.[modKey] || {}), approve_limit: value === '' ? null : parseFloat(value) || null }
      },
      _dirty: true,
    }));

  const setAllForCol = (action, value) => {
    if (!canEdit || isSuperAdminSelected) return;
    setRoles((rs) => rs.map((r) => {
      if (r.id !== selectedId) return r;
      const next = { ...(r.permissions || {}) };
      for (const m of catalog.modules) {
        next[m.key] = { ...(next[m.key] || {}), [action]: value };
      }
      return { ...r, permissions: next, _dirty: true };
    }));
  };

  const setAllForCategory = (cat, value) => {
    if (!canEdit || isSuperAdminSelected) return;
    const modsInCat = (catalog.modules || []).filter((m) => m.category === cat);
    setRoles((rs) => rs.map((r) => {
      if (r.id !== selectedId) return r;
      const next = { ...(r.permissions || {}) };
      for (const m of modsInCat) {
        next[m.key] = Object.fromEntries(catalog.actions.map((a) => [a, value]));
      }
      return { ...r, permissions: next, _dirty: true };
    }));
  };

  // true when EVERY action × EVERY module in a category is granted
  const isCategoryFullyChecked = (cat) => {
    if (isSuperAdminSelected) return true;
    if (!selected) return false;
    const modsInCat = (catalog.modules || []).filter((m) => m.category === cat);
    if (modsInCat.length === 0) return false;
    return modsInCat.every((m) =>
      catalog.actions.every((a) => !!selected.permissions?.[m.key]?.[a])
    );
  };

  const updateMeta = (field, value) => {
    setRoles((rs) => rs.map((r) => (r.id === selectedId ? { ...r, [field]: value, _dirty: true } : r)));
  };

  const [sodViolations, setSodViolations] = useState([]);
  const [sodAcknowledged, setSodAcknowledged] = useState(false);

  const saveRole = async (acknowledgeOverride = false) => {
    if (!selected) return;
    setSaving(true);
    try {
      const res = await apiPut(`/rbac/roles/${selected.id}`, {
        name: selected.name,
        grade: selected.grade,
        department: selected.department,
        description: selected.description,
        permissions: selected.permissions,
        acknowledge_sod: acknowledgeOverride || sodAcknowledged,
      });
      // BUILD 2: SoD warning — backend returns sod_warning flag
      if (res?.sod_warning) {
        setSodViolations(res.violations || []);
        setSaving(false);
        return;
      }
      setSodViolations([]);
      setSodAcknowledged(false);
      toast.success(`${selected.name} saved`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const createCustomRole = async () => {
    const name = window.prompt('Name for the new custom role?');
    if (!name) return;
    try {
      const r = await apiPost('/rbac/roles', {
        name, grade: null, department: null, description: 'Custom role',
        permissions: {},
      });
      toast.success(`Created '${name}'`);
      setSelectedId(r.id);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not create role');
    }
  };

  const cloneRole = async () => {
    if (!selected) return;
    const name = window.prompt(`Clone "${selected.name}" as:`, `${selected.name} (Copy)`);
    if (!name) return;
    try {
      const r = await apiPost('/rbac/roles', {
        name,
        grade: selected.grade,
        department: selected.department,
        description: `Cloned from ${selected.name}`,
        permissions: selected.permissions,
      });
      toast.success(`Cloned as '${name}'`);
      setSelectedId(r.id);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not clone');
    }
  };

  const deleteRole = async () => {
    if (!selected || selected.is_system) return;
    if (!window.confirm(`Delete role "${selected.name}"?\n\nUsers currently on this role will be detached and need a new role assigned.`)) return;
    try {
      const r = await apiDel(`/rbac/roles/${selected.id}`);
      toast.success(`Deleted (${r.detached_users || 0} user${r.detached_users === 1 ? '' : 's'} detached)`);
      setSelectedId(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  if (loading) {
    return <div className="p-10 text-center"><Loader2 size={20} className="animate-spin inline mr-2" />Loading roles…</div>;
  }

  return (
    <div className="p-6 md:p-8" data-testid="rbac-page">
      <button onClick={() => navigate('/app/settings')} className="text-xs font-display font-bold uppercase tracking-wider text-slate-500 hover:text-[#1F7A6E] flex items-center gap-1 mb-3"><ArrowLeft size={12} /> Back to Settings</button>
      <PageHeader
        overline="Settings · Security"
        title="Roles & Access Control"
        subtitle="Module × action permissions for every role. Edit existing roles, create custom ones, or assign roles to users."
        icon={ShieldAlert}
      />

      {!canEdit && (
        <div className="mt-4 bg-amber-50 border border-amber-200 rounded-sm px-4 py-3 text-xs text-amber-900">
          <strong>Read-only view.</strong> Only Super Admin and IT department can edit roles.
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 mt-4">
        {/* Left — Roles list */}
        <aside className="lg:col-span-3 bg-white border border-slate-200 rounded-sm p-3" data-testid="roles-sidebar">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs font-display font-bold uppercase tracking-widest text-slate-600">Roles ({roles.length})</div>
            {canEdit && (
              <button onClick={createCustomRole} className="text-[10px] font-display font-bold uppercase tracking-wider text-[#1F7A6E] hover:text-[#14504E] flex items-center gap-1" data-testid="rbac-new-role">
                <Plus size={11} /> New
              </button>
            )}
          </div>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search role…"
            className="w-full px-2.5 py-1.5 mb-2 border border-slate-200 rounded-sm text-xs focus:outline-none focus:border-[#1F7A6E]"
            data-testid="rbac-search"
          />
          <ul className="space-y-1 max-h-[60vh] overflow-y-auto">
            {filteredRoles.map((r) => (
              <li key={r.id}>
                <button
                  onClick={() => setSelectedId(r.id)}
                  className={`w-full text-left px-2.5 py-2 rounded-sm transition-colors ${selectedId === r.id ? 'bg-teal-50 border border-[#1F7A6E]/30' : 'border border-transparent hover:bg-slate-50'}`}
                  data-testid={`rbac-role-${r.id}`}
                >
                  <div className="flex items-center gap-1.5">
                    <span className="text-sm font-display font-bold text-[#0F172A] truncate">{r.name}</span>
                    {r.is_system && <span className="text-[9px] font-mono bg-slate-100 text-slate-500 px-1 rounded">SYS</span>}
                    {r._dirty && <span className="text-[9px] font-mono bg-amber-100 text-amber-700 px-1 rounded">●</span>}
                  </div>
                  <div className="text-[10px] text-slate-500 mt-0.5">
                    {[r.grade, r.department].filter(Boolean).join(' · ') || 'No grade / dept'}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </aside>

        {/* Right — Role detail + matrix */}
        <main className="lg:col-span-9 space-y-3">
          {!selected ? (
            <div className="bg-white border border-slate-200 rounded-sm p-10 text-center text-sm text-slate-400">Pick a role from the left</div>
          ) : (
            <>
              {/* Meta */}
              <div className="bg-white border border-slate-200 rounded-sm p-4 grid grid-cols-1 md:grid-cols-12 gap-3" data-testid="rbac-role-meta">
                <div className="md:col-span-4">
                  <label className="text-[10px] uppercase tracking-widest font-display font-bold text-slate-500 block mb-1">Role Name</label>
                  <input
                    value={selected.name || ''}
                    onChange={(e) => updateMeta('name', e.target.value)}
                    disabled={!canEdit || isSuperAdminSelected}
                    className="w-full px-2.5 py-1.5 border border-slate-300 rounded-sm text-sm focus:outline-none focus:border-[#1F7A6E] disabled:bg-slate-50"
                    data-testid="rbac-role-name"
                  />
                </div>
                <div className="md:col-span-3">
                  <label className="text-[10px] uppercase tracking-widest font-display font-bold text-slate-500 block mb-1">Grade</label>
                  <select
                    value={selected.grade || ''}
                    onChange={(e) => updateMeta('grade', e.target.value || null)}
                    disabled={!canEdit || isSuperAdminSelected}
                    className="w-full px-2.5 py-1.5 border border-slate-300 rounded-sm text-sm focus:outline-none focus:border-[#1F7A6E] bg-white disabled:bg-slate-50"
                    data-testid="rbac-role-grade"
                  >
                    <option value="">—</option>
                    {grades.map((g) => <option key={g} value={g.split(' ')[0]}>{g}</option>)}
                  </select>
                </div>
                <div className="md:col-span-3">
                  <label className="text-[10px] uppercase tracking-widest font-display font-bold text-slate-500 block mb-1">Department</label>
                  <select
                    value={selected.department || ''}
                    onChange={(e) => updateMeta('department', e.target.value || null)}
                    disabled={!canEdit || isSuperAdminSelected}
                    className="w-full px-2.5 py-1.5 border border-slate-300 rounded-sm text-sm focus:outline-none focus:border-[#1F7A6E] bg-white disabled:bg-slate-50"
                    data-testid="rbac-role-dept"
                  >
                    <option value="">—</option>
                    {departments.map((d) => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
                <div className="md:col-span-2 flex items-end gap-1">
                  {canEdit && !isSuperAdminSelected && (
                    <>
                      <button onClick={cloneRole} className="px-2 py-1.5 border border-slate-300 rounded-sm text-[10px] font-display font-bold uppercase tracking-wider hover:bg-slate-50" data-testid="rbac-clone" title="Clone this role">
                        <Copy size={11} />
                      </button>
                      {!selected.is_system && (
                        <button onClick={deleteRole} className="px-2 py-1.5 border border-rose-300 text-rose-600 rounded-sm text-[10px] font-display font-bold uppercase tracking-wider hover:bg-rose-50" data-testid="rbac-delete" title="Delete this role">
                          <Trash2 size={11} />
                        </button>
                      )}
                    </>
                  )}
                </div>
                <div className="md:col-span-12">
                  <label className="text-[10px] uppercase tracking-widest font-display font-bold text-slate-500 block mb-1">Description</label>
                  <input
                    value={selected.description || ''}
                    onChange={(e) => updateMeta('description', e.target.value)}
                    disabled={!canEdit || isSuperAdminSelected}
                    className="w-full px-2.5 py-1.5 border border-slate-300 rounded-sm text-sm focus:outline-none focus:border-[#1F7A6E] disabled:bg-slate-50"
                  />
                </div>
              </div>

              {isSuperAdminSelected && (
                <div className="bg-amber-50 border border-amber-200 rounded-sm px-4 py-2.5 text-xs text-amber-900">
                  <strong>Super Admin permissions are locked.</strong> Every toggle is always ON, and nobody — including other Super Admins — can downgrade this role through the UI.
                </div>
              )}

              {/* Matrix */}
              <div className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="rbac-matrix">
                <div className="overflow-x-auto max-h-[55vh]">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 sticky top-0 z-10">
                      <tr>
                        <th className="text-left py-2 px-3 w-72 font-display font-bold uppercase tracking-widest text-[10px] text-slate-500">Module</th>
                        {catalog.actions.map((a) => (
                          <th key={a} className="py-2 px-2 font-display font-bold uppercase tracking-widest text-[10px] text-slate-500 text-center">
                            <div className="capitalize">{a}</div>
                            {canEdit && !isSuperAdminSelected && (
                              <div className="flex justify-center gap-1 mt-0.5">
                                <button onClick={() => setAllForCol(a, true)} className="text-emerald-600 hover:text-emerald-800" title={`All ${a} = ON`} data-testid={`rbac-col-on-${a}`}><Check size={9}/></button>
                                <button onClick={() => setAllForCol(a, false)} className="text-rose-500 hover:text-rose-700" title={`All ${a} = OFF`} data-testid={`rbac-col-off-${a}`}><X size={9}/></button>
                              </div>
                            )}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(grouped).map(([cat, mods]) => {
                        const allOn = isCategoryFullyChecked(cat);
                        return (
                        <React.Fragment key={cat}>
                          <tr className="bg-slate-100/70">
                            <td className="px-3 py-1.5">
                              <label className="flex items-center gap-2 cursor-pointer select-none">
                                <input
                                  type="checkbox"
                                  checked={allOn}
                                  disabled={!canEdit || isSuperAdminSelected}
                                  onChange={() => setAllForCategory(cat, !allOn)}
                                  className="w-3.5 h-3.5 accent-[#1F7A6E] cursor-pointer disabled:cursor-default"
                                  data-testid={`rbac-cat-selectall-${cat.toLowerCase().replace(/\s+/g, '-')}`}
                                  title={`Select all ${cat} permissions`}
                                />
                                <span className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-600">
                                  {cat}
                                </span>
                                <span className="text-[9px] text-slate-400 font-normal normal-case tracking-normal">
                                  ({mods.length} module{mods.length === 1 ? '' : 's'})
                                </span>
                              </label>
                            </td>
                            <td colSpan={catalog.actions.length} className="px-3 py-1.5 text-right text-[9px] text-slate-400">
                              {allOn ? 'all granted' : 'click ☑ to grant all in this category'}
                            </td>
                          </tr>
                          {mods.map((m) => {
                            const perms = selected.permissions?.[m.key] || {};
                            const hasApprove = !!perms.approve;
                            const scope = perms.scope || 'all';
                            const approveLimit = perms.approve_limit ?? '';
                            return (
                              <React.Fragment key={m.key}>
                                <tr className="border-t border-slate-100 hover:bg-slate-50/50">
                                  <td className="px-3 py-1.5">
                                    <div className="font-display font-semibold text-[#0F172A] truncate">{m.label}</div>
                                    <div className="text-[9px] font-mono text-slate-400">{m.key}</div>
                                  </td>
                                  {catalog.actions.map((a) => (
                                    <td key={a} className="text-center py-1.5">
                                      <input
                                        type="checkbox"
                                        checked={isSuperAdminSelected ? true : !!perms[a]}
                                        disabled={!canEdit || isSuperAdminSelected}
                                        onChange={() => togglePerm(m.key, a)}
                                        className="w-4 h-4 accent-[#1F7A6E] cursor-pointer disabled:cursor-default"
                                        data-testid={`rbac-cell-${m.key}-${a}`}
                                      />
                                    </td>
                                  ))}
                                </tr>
                                {/* BUILD 2: scope + limit sub-row — only when approve is ON */}
                                {hasApprove && !isSuperAdminSelected && canEdit && (
                                  <tr className="bg-teal-50/40 border-b border-teal-100">
                                    <td colSpan={catalog.actions.length + 1} className="px-3 py-2">
                                      <div className="flex flex-wrap items-center gap-4">
                                        {/* Scope */}
                                        <div className="flex items-center gap-1.5">
                                          <span className="text-[10px] font-display font-bold uppercase tracking-wider text-slate-500">Scope</span>
                                          <select
                                            value={scope}
                                            onChange={e => setModuleScope(m.key, e.target.value)}
                                            className="text-xs border border-slate-200 rounded-sm px-2 py-0.5 bg-white focus:outline-none focus:border-[#1F7A6E]"
                                            data-testid={`rbac-scope-${m.key}`}
                                          >
                                            <option value="all">All Projects</option>
                                            <option value="assigned">Assigned Projects Only</option>
                                          </select>
                                        </div>
                                        {/* Approve limit */}
                                        <div className="flex items-center gap-1.5">
                                          <span className="text-[10px] font-display font-bold uppercase tracking-wider text-slate-500">Approve Limit (₹)</span>
                                          <input
                                            type="number" min="0" step="1000"
                                            value={approveLimit}
                                            onChange={e => setApproveLimit(m.key, e.target.value)}
                                            placeholder="Unlimited"
                                            className="w-28 text-xs border border-slate-200 rounded-sm px-2 py-0.5 focus:outline-none focus:border-[#1F7A6E]"
                                            data-testid={`rbac-limit-${m.key}`}
                                          />
                                          {approveLimit !== '' && approveLimit !== null && (
                                            <span className="text-[10px] text-slate-400">= ₹{Number(approveLimit).toLocaleString('en-IN')}</span>
                                          )}
                                        </div>
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Users assigned to this role */}
              <div className="bg-white border border-slate-200 rounded-sm" data-testid="rbac-role-users">
                <div className="px-4 py-2.5 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <UsersIcon size={14} className="text-[#1F7A6E]" />
                    <span className="text-xs font-display font-bold uppercase tracking-widest text-slate-600">
                      Users in this role ({roleUsers.length})
                    </span>
                  </div>
                  <span className="text-[10px] text-slate-500">Reassign individuals to a different role without leaving this page.</span>
                </div>
                {loadingUsers ? (
                  <div className="px-4 py-6 text-center text-xs text-slate-400"><Loader2 size={14} className="animate-spin inline mr-2" />Loading users…</div>
                ) : roleUsers.length === 0 ? (
                  <div className="px-4 py-6 text-center text-xs text-slate-400">No users are currently assigned to this role.</div>
                ) : (
                  <ul className="divide-y divide-slate-100 max-h-72 overflow-y-auto">
                    {roleUsers.map(u => (
                      <li key={u.user_id} className="px-4 py-2 flex items-center justify-between gap-3" data-testid={`role-user-${u.user_id}`}>
                        <div className="min-w-0 flex-1">
                          <div className="font-display font-semibold text-sm text-[#0F172A] truncate">{u.name || u.email}</div>
                          <div className="text-[10px] text-slate-500 uppercase tracking-wide">
                            {u.email}{u.department ? ` · ${u.department}` : ''}{u.employee_id ? ` · ${u.employee_id}` : ''}
                          </div>
                        </div>
                        {canEdit && (u.role || '').toLowerCase() !== 'super_admin' && (
                          reassignFor === u.user_id ? (
                            <div className="flex items-center gap-1.5">
                              <select
                                value={reassignTarget}
                                onChange={(e) => setReassignTarget(e.target.value)}
                                className="px-2 py-1 border border-slate-300 rounded-sm text-xs bg-white"
                                data-testid={`reassign-target-${u.user_id}`}
                              >
                                <option value="">Pick role…</option>
                                <option value="_unassigned">— No role (legacy defaults) —</option>
                                {roles.filter(r => r.id !== selectedId && r.id !== 'role_super_admin').map(r => (
                                  <option key={r.id} value={r.id}>{r.name}{r.is_system ? ' [System]' : ' [Custom]'}</option>
                                ))}
                              </select>
                              <button
                                onClick={() => reassignUserRole(u.user_id, reassignTarget)}
                                disabled={!reassignTarget}
                                className="px-2 py-1 bg-[#1F7A6E] hover:bg-[#14504E] disabled:opacity-40 text-white text-[10px] font-display font-bold uppercase tracking-wider rounded-sm"
                                data-testid={`reassign-confirm-${u.user_id}`}
                              >Move</button>
                              <button
                                onClick={() => { setReassignFor(null); setReassignTarget(''); }}
                                className="px-2 py-1 border border-slate-300 text-slate-600 text-[10px] font-display font-bold uppercase tracking-wider rounded-sm hover:bg-slate-50"
                              >Cancel</button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1.5">
                              <button
                                onClick={() => openPermEditor(u)}
                                className="inline-flex items-center gap-1 px-2.5 py-1 border border-[#1F7A6E]/40 text-[#1F7A6E] hover:bg-teal-50 text-[10px] font-display font-bold uppercase tracking-wider rounded-sm"
                                data-testid={`edit-access-${u.user_id}`}
                                title="Edit this individual's permissions (per-user override)"
                              >
                                Edit access
                              </button>
                              <button
                                onClick={() => { setReassignFor(u.user_id); setReassignTarget(''); }}
                                className="inline-flex items-center gap-1 px-2.5 py-1 border border-slate-300 hover:border-[#1F7A6E] hover:text-[#1F7A6E] text-[10px] font-display font-bold uppercase tracking-wider rounded-sm"
                                data-testid={`reassign-${u.user_id}`}
                              >
                                Change role <ChevronRight size={10} />
                              </button>
                            </div>
                          )
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* BUILD 2: SoD warning banner */}
              {sodViolations.length > 0 && (
                <div className="mx-4 mb-3 p-3 bg-amber-50 border border-amber-300 rounded-sm" data-testid="sod-warning-banner">
                  <div className="font-display font-bold text-amber-800 text-xs uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                    ⚠ Segregation of Duties Conflict
                  </div>
                  <ul className="text-xs text-amber-700 space-y-1 mb-2">
                    {sodViolations.map((v, i) => <li key={i}>• {v.message}</li>)}
                  </ul>
                  <div className="flex items-center gap-3">
                    <label className="flex items-center gap-1.5 text-xs cursor-pointer text-amber-800">
                      <input type="checkbox" checked={sodAcknowledged} onChange={e => setSodAcknowledged(e.target.checked)} />
                      I acknowledge this SoD conflict and accept the audit log entry
                    </label>
                    <button
                      disabled={!sodAcknowledged || saving}
                      onClick={() => saveRole(true)}
                      className="px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white text-xs font-bold rounded disabled:opacity-40"
                      data-testid="sod-confirm-save"
                    >
                      Confirm Save Anyway
                    </button>
                  </div>
                </div>
              )}
              {/* Save bar */}
              {canEdit && !isSuperAdminSelected && (
                <div className="flex items-center justify-end gap-2 sticky bottom-0 bg-white/95 backdrop-blur py-3 border-t border-slate-200">
                  <button onClick={load} className="px-4 py-2 border border-slate-300 rounded-sm text-xs font-display font-bold uppercase tracking-wider hover:bg-slate-50">Discard</button>
                  <button onClick={() => saveRole(false)} disabled={saving || !selected._dirty} className="inline-flex items-center gap-1.5 px-5 py-2 bg-[#1F7A6E] hover:bg-[#14504E] text-white text-xs font-display font-bold uppercase tracking-wider rounded-sm disabled:opacity-40 disabled:cursor-not-allowed" data-testid="rbac-save">
                    {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                    {saving ? 'Saving…' : selected._dirty ? 'Save Changes' : 'No Changes'}
                  </button>
                </div>
              )}
            </>
          )}
        </main>
      </div>

      {/* Per-user permission override modal */}
      {permEditFor && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-4xl max-h-[90vh] rounded-sm shadow-xl flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="user-perm-modal">
            <div className="px-5 py-3 border-b border-slate-200 flex items-start justify-between">
              <div>
                <div className="text-[10px] font-display font-bold uppercase tracking-[0.18em] text-[#1F7A6E]">Per-user override</div>
                <h2 className="font-display font-bold text-lg mt-0.5">{permEditFor.name}</h2>
                <div className="text-xs text-slate-500">{permEditFor.email} · Inherits from role: <strong>{permEditFor.role_name}</strong></div>
              </div>
              <button onClick={() => setPermEditFor(null)} className="p-1.5 hover:bg-slate-100 rounded-sm"><X size={18} /></button>
            </div>
            <div className="px-5 py-2.5 bg-amber-50 border-b border-amber-200 text-[11px] text-amber-900 flex items-center gap-2">
              Checkboxes shown reflect this user's <strong>effective access</strong>. Cells that differ from the role get marked with a yellow dot. Click <strong>Reset</strong> to remove every override and follow the role exactly.
            </div>
            <div className="flex-1 overflow-y-auto">
              {permEditLoading ? (
                <div className="py-16 text-center text-sm text-slate-400"><Loader2 size={16} className="animate-spin inline mr-2" />Loading…</div>
              ) : (
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 sticky top-0 z-10">
                    <tr>
                      <th className="text-left py-2 px-3 w-72 font-display font-bold uppercase tracking-widest text-[10px] text-slate-500">Module</th>
                      {catalog.actions.map((a) => (
                        <th key={a} className="py-2 px-2 font-display font-bold uppercase tracking-widest text-[10px] text-slate-500 text-center capitalize">{a}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(grouped).map(([cat, mods]) => (
                      <React.Fragment key={cat}>
                        <tr className="bg-slate-100/70">
                          <td colSpan={1 + catalog.actions.length} className="px-3 py-1.5 text-[10px] font-display font-bold uppercase tracking-widest text-slate-600">{cat}</td>
                        </tr>
                        {mods.map((m) => {
                          const perms = permEditMatrix[m.key] || {};
                          const rolePerms = permEditRoleBase[m.key] || {};
                          return (
                            <tr key={m.key} className="border-t border-slate-100 hover:bg-slate-50/50">
                              <td className="px-3 py-1.5">
                                <div className="font-display font-semibold text-[#0F172A] truncate">{m.label}</div>
                                <div className="text-[9px] font-mono text-slate-400">{m.key}</div>
                              </td>
                              {catalog.actions.map((a) => {
                                const isOverridden = !!perms[a] !== !!rolePerms[a];
                                return (
                                  <td key={a} className="text-center py-1.5 relative">
                                    <input
                                      type="checkbox"
                                      checked={!!perms[a]}
                                      onChange={() => togglePermEditCell(m.key, a)}
                                      className="w-4 h-4 accent-[#1F7A6E] cursor-pointer"
                                      data-testid={`user-perm-${m.key}-${a}`}
                                    />
                                    {isOverridden && (
                                      <span className="absolute top-1 right-3 w-1.5 h-1.5 rounded-full bg-amber-500" title="Differs from role default" />
                                    )}
                                  </td>
                                );
                              })}
                            </tr>
                          );
                        })}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-between gap-2">
              <button
                onClick={resetPermEdit}
                disabled={permEditSaving || Object.keys(permEditOverrides).length === 0}
                className="px-3 py-1.5 border border-rose-300 text-rose-700 rounded-sm text-xs font-display font-bold uppercase tracking-wider hover:bg-rose-50 disabled:opacity-40"
                data-testid="user-perm-reset"
                title={Object.keys(permEditOverrides).length === 0 ? 'No overrides to reset' : 'Remove all per-user overrides'}
              >
                Reset to role
              </button>
              <div className="flex items-center gap-2">
                <button onClick={() => setPermEditFor(null)} className="px-4 py-1.5 border border-slate-300 rounded-sm text-xs font-display font-bold uppercase tracking-wider hover:bg-slate-50">Cancel</button>
                <button
                  onClick={savePermEdit}
                  disabled={permEditSaving}
                  className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-[#1F7A6E] hover:bg-[#14504E] text-white text-xs font-display font-bold uppercase tracking-wider rounded-sm disabled:opacity-40"
                  data-testid="user-perm-save"
                >
                  {permEditSaving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                  {permEditSaving ? 'Saving…' : 'Save Overrides'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
